"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetCampaignDetails = exports.googleAdsGetCampaignDetailsResultSchema = exports.googleAdsGetCampaignDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetCampaignDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically iterate through all accessible accounts and aggregate results. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific campaign ID to analyze. If not provided, returns data for all campaigns.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. If omitted, returns lifetime data from campaign creation.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. If omitted, returns data up to today.'),
    metrics: zod_1.z
        .array(zod_1.z.enum(['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc', 'cpm']))
        .nullish()
        .default(['impressions', 'clicks', 'cost', 'ctr'])
        .describe('Metrics to include in the response. Defaults to basic metrics.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of campaigns to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    includeDisabled: zod_1.z
        .boolean()
        .default(false)
        .describe('Include paused, removed, or disabled campaigns in results. Set to true to show all campaigns regardless of status. Defaults to false.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no campaign data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
    })
        .describe('Performance query options'),
})
    .describe('Get Google Ads campaign performance metrics. Use for raw data export, workflows, or when user explicitly wants numbers without analysis. For analysis, insights, or recommendations, use googleAdsInsights instead.');
exports.googleAdsGetCampaignDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetCampaignDetailsInputSchema);
var googleAdsGetCampaignDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    campaignId: zod_1.z.string(),
    campaignName: zod_1.z.string().nullish(),
    advertiserId: zod_1.z.string(),
    status: zod_1.z.enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED']).nullish(),
    campaignType: zod_1.z.string().nullish(),
    biddingStrategy: zod_1.z.string().nullish(),
    servingStatus: zod_1.z.string().nullish(),
    startDate: zod_1.z.string().nullish(),
    endDate: zod_1.z.string().nullish(),
    budget: zod_1.z
        .object({
        amount: zod_1.z.number().nullish(),
        currency: zod_1.z.string().nullish(),
        type: zod_1.z.enum(['DAILY', 'TOTAL']).nullish(),
    })
        .nullish(),
    performance: zod_1.z.object({
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
        conversions: zod_1.z.number().nullish(),
        ctr: zod_1.z.number().nullish(),
        cpc: zod_1.z.number().nullish(),
        cpm: zod_1.z.number().nullish(),
        conversionRate: zod_1.z.number().nullish(),
    }),
    targeting: zod_1.z
        .object({
        locations: zod_1.z.array(zod_1.z.string()).nullish(),
        languages: zod_1.z.array(zod_1.z.string()).nullish(),
        keywords: zod_1.z.array(zod_1.z.string()).nullish(),
    })
        .nullish(),
    adGroups: zod_1.z
        .array(zod_1.z.object({
        adGroupId: zod_1.z.string(),
        adGroupName: zod_1.z.string().nullish(),
        status: zod_1.z.string().nullish(),
    }))
        .nullish()
        .describe('List of ad groups within this campaign'),
    ads: zod_1.z
        .array(zod_1.z.object({
        adId: zod_1.z.string(),
        adGroupId: zod_1.z.string(),
        adType: zod_1.z.string().nullish(),
        status: zod_1.z.string().nullish(),
    }))
        .nullish()
        .describe('List of ads within this campaign'),
    dailyBreakdown: zod_1.z
        .array(zod_1.z.object({
        date: zod_1.z.string(),
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
    }))
        .nullish(),
}));
exports.googleAdsGetCampaignDetailsResultSchema = googleAdsGetCampaignDetailsResponseSchema;
exports.googleAdsGetCampaignDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetCampaignDetails',
    displayName: 'Google Ads Campaign Performance',
    displayDescription: 'Get performance metrics and analytics for Google Ads campaigns. Automatically searches all accessible accounts unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetCampaignDetailsArgsSchema,
}, googleAdsGetCampaignDetailsResponseSchema);
